import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  MenuItem,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  getSetting,
  saveSetting,
  DASHBOARD_KEYS,
  CATEGORY,
} from "../../../service/BuyerConfiguration/BuyerAppDashboard/buyerAppDashboardService";
import { HomeTabsManager } from "./HomeTabsManager";
import {
  HomeTheme,
  listThemes,
} from "../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { statusChip } from "./ThemeStudio/themeUtils";

const GREEN = "#00B14F";

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <Card sx={{ mb: 2.5, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
    <CardContent>
      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5, mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

export const BuyerAppDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [toast, setToast] = React.useState<{ msg: string; ok: boolean } | null>(null);

  // ₹99 look & animation
  const [bg, setBg] = React.useState("");
  const [accent, setAccent] = React.useState("");
  const [animStyle, setAnimStyle] = React.useState("auto-scroll");
  const [animSpeed, setAnimSpeed] = React.useState("40");
  // rotating titles
  const [titles, setTitles] = React.useState("");
  // map pin
  const [pinUrl, setPinUrl] = React.useState("");
  // loader
  const [lottieUrl, setLottieUrl] = React.useState("");
  const [loaderLines, setLoaderLines] = React.useState("");

  const [saving, setSaving] = React.useState<string | null>(null);
  // Theme showing now: undefined = loading, null = failed.
  const [liveTheme, setLiveTheme] = React.useState<HomeTheme | null | undefined>(undefined);
  const showToast = React.useCallback(
    (msg: string, ok: boolean) => setToast({ msg, ok }),
    [],
  );

  React.useEffect(() => {
    listThemes()
      .then((r) => {
        const t = r.themes.find((x) => x.id === r.active_id) ?? r.themes.find((x) => x.is_default) ?? null;
        setLiveTheme(t);
      })
      .catch(() => setLiveTheme(null));
  }, []);

  React.useEffect(() => {
    (async () => {
      setBg(await getSetting(DASHBOARD_KEYS.storeBg));
      setAccent(await getSetting(DASHBOARD_KEYS.storeAccent));
      const st = await getSetting(DASHBOARD_KEYS.storeAnimStyle);
      if (st) setAnimStyle(st);
      const sp = await getSetting(DASHBOARD_KEYS.storeAnimSpeed);
      if (sp) setAnimSpeed(sp);
      const t = await getSetting(DASHBOARD_KEYS.restaurantTitles);
      try {
        const arr = t ? JSON.parse(t) : [];
        if (Array.isArray(arr)) setTitles(arr.join("\n"));
      } catch {
        /* ignore */
      }
      setPinUrl(await getSetting(DASHBOARD_KEYS.mapPinUrl));
      setLottieUrl(await getSetting(DASHBOARD_KEYS.loaderLottieUrl));
      const ll = await getSetting(DASHBOARD_KEYS.loaderLines);
      try {
        const arr = ll ? JSON.parse(ll) : [];
        if (Array.isArray(arr)) setLoaderLines(arr.join("\n"));
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const save = async (id: string, fn: () => Promise<any>) => {
    setSaving(id);
    try {
      await fn();
      setToast({ msg: "Saved. The app picks it up on its next load.", ok: true });
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.message || "Save failed. Try again.", ok: false });
    } finally {
      setSaving(null);
    }
  };

  const linesToJson = (text: string) =>
    JSON.stringify(
      text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    );

  return (
    <Box sx={{ p: 3, maxWidth: 780 }}>
      <Typography sx={{ fontWeight: 800, fontSize: 24, mb: 0.5 }}>
        Buyer App Dashboard
      </Typography>
      <Typography sx={{ color: "text.secondary", fontSize: 14, mb: 3 }}>
        Manage the buyer app’s home look & feel. Changes apply on the app’s next load.
      </Typography>

      <SectionCard
        title="Home tabs"
        subtitle="The cards at the top of the home screen. Food is always first; each tab you add lists the restaurants and dishes you pick."
      >
        <HomeTabsManager onToast={showToast} />
      </SectionCard>

      <SectionCard
        title="Theme Studio"
        subtitle="Festival & season looks for the top of the buyer home. Changes reach open apps within seconds."
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Live now</Typography>
            {liveTheme === undefined ? (
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Loading…</Typography>
            ) : liveTheme === null ? (
              <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Could not load the current theme.</Typography>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{liveTheme.name}</Typography>
                <Chip
                  size="small"
                  label={liveTheme.is_default ? "Default" : statusChip(liveTheme).label}
                  sx={{ bgcolor: statusChip(liveTheme).bg, color: statusChip(liveTheme).color, fontWeight: 600 }}
                />
              </Stack>
            )}
          </Box>
          <Button
            variant="contained"
            sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", borderRadius: 2 }}
            onClick={() => navigate("/buyer-app-dashboard/theme-studio")}
          >
            Open Theme Studio →
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Card Studio"
        subtitle="How restaurant cards look on the buyer home — the look for all cards, and photos, badge, tagline and chips per restaurant."
      >
        <Button
          variant="contained"
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", borderRadius: 2 }}
          onClick={() => navigate("/buyer-app-dashboard/card-studio")}
        >
          Open Card Studio →
        </Button>
      </SectionCard>

      <SectionCard
        title="Banner cards"
        subtitle="The three boxes under the search bar — shape, style, and how they move: row, carousel or a running train."
      >
        <Button
          variant="contained"
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", borderRadius: 2 }}
          onClick={() => navigate("/buyer-app-dashboard/banner-cards")}
        >
          Open Banner Cards Studio →
        </Button>
      </SectionCard>

      <SectionCard
        title="What's on your mind?"
        subtitle="The food categories row on the buyer home: icons, order, on/off, the words and price each one lists, and the section title."
      >
        <Button
          variant="contained"
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", borderRadius: 2 }}
          onClick={() => navigate("/dish-management")}
        >
          Manage Dish Management →
        </Button>
      </SectionCard>

      <SectionCard
        title="₹99 Store"
        subtitle="Manage deals, hero media and the section labels."
      >
        <Button
          variant="contained"
          sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#0a7a3c" }, textTransform: "none", borderRadius: 2 }}
          onClick={() => navigate("/store-deals")}
        >
          Manage ₹99 Store →
        </Button>
      </SectionCard>

      <SectionCard
        title="₹99 Look & Animation"
        subtitle="Section background, accent colour and the rolling animation."
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField label="Background colour (hex)" size="small" fullWidth value={bg} onChange={(e) => setBg(e.target.value)} placeholder="#E8501C" />
          <TextField label="Accent colour (hex)" size="small" fullWidth value={accent} onChange={(e) => setAccent(e.target.value)} placeholder="#E8501C" />
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
          <TextField select label="Animation" size="small" fullWidth value={animStyle} onChange={(e) => setAnimStyle(e.target.value)}>
            <MenuItem value="auto-scroll">Auto-scroll (rolling)</MenuItem>
            <MenuItem value="none">None (static)</MenuItem>
          </TextField>
          <TextField label="Speed (px/sec)" size="small" type="number" fullWidth value={animSpeed} onChange={(e) => setAnimSpeed(e.target.value)} />
        </Stack>
        <Button
          variant="outlined"
          sx={{ mt: 2, textTransform: "none", borderColor: GREEN, color: GREEN }}
          disabled={saving === "look"}
          onClick={() =>
            save("look", async () => {
              await saveSetting(DASHBOARD_KEYS.storeBg, bg, CATEGORY.storeDeal);
              await saveSetting(DASHBOARD_KEYS.storeAccent, accent, CATEGORY.storeDeal);
              await saveSetting(DASHBOARD_KEYS.storeAnimStyle, animStyle, CATEGORY.storeDeal);
              await saveSetting(DASHBOARD_KEYS.storeAnimSpeed, animSpeed, CATEGORY.storeDeal);
            })
          }
        >
          {saving === "look" ? "Saving…" : "Save look & animation"}
        </Button>
      </SectionCard>

      <SectionCard
        title="“Restaurants near you” titles"
        subtitle="One name per line. The app shuffles them and changes every 3s."
      >
        <TextField multiline minRows={6} fullWidth value={titles} onChange={(e) => setTitles(e.target.value)} placeholder={"Top picks near you\nHungry? Start here\n..."} />
        <Button
          variant="outlined"
          sx={{ mt: 2, textTransform: "none", borderColor: GREEN, color: GREEN }}
          disabled={saving === "titles"}
          onClick={() => save("titles", () => saveSetting(DASHBOARD_KEYS.restaurantTitles, linesToJson(titles), CATEGORY.home))}
        >
          {saving === "titles" ? "Saving…" : "Save titles"}
        </Button>
      </SectionCard>

      <SectionCard
        title="Map pin icon"
        subtitle="A PNG URL for the location pin — e.g. a 3D landmark (Eiffel Tower, Burj Khalifa)."
      >
        <TextField label="Pin image URL" size="small" fullWidth value={pinUrl} onChange={(e) => setPinUrl(e.target.value)} placeholder="https://…/pin.png" />
        <Button
          variant="outlined"
          sx={{ mt: 2, textTransform: "none", borderColor: GREEN, color: GREEN }}
          disabled={saving === "pin"}
          onClick={() => save("pin", () => saveSetting(DASHBOARD_KEYS.mapPinUrl, pinUrl, CATEGORY.map))}
        >
          {saving === "pin" ? "Saving…" : "Save pin icon"}
        </Button>
      </SectionCard>

      <SectionCard
        title="App loader"
        subtitle="A Lottie animation URL (.json) + rotating lines shown while screens load."
      >
        <TextField label="Lottie JSON URL" size="small" fullWidth value={lottieUrl} onChange={(e) => setLottieUrl(e.target.value)} placeholder="https://…/loader.json" />
        <Divider sx={{ my: 2 }} />
        <TextField label="Loader lines (one per line)" multiline minRows={4} fullWidth value={loaderLines} onChange={(e) => setLoaderLines(e.target.value)} placeholder={"Plating your cravings…\nWarming up the kitchen…"} />
        <Button
          variant="outlined"
          sx={{ mt: 2, textTransform: "none", borderColor: GREEN, color: GREEN }}
          disabled={saving === "loader"}
          onClick={() =>
            save("loader", async () => {
              await saveSetting(DASHBOARD_KEYS.loaderLottieUrl, lottieUrl, CATEGORY.loader);
              await saveSetting(DASHBOARD_KEYS.loaderLines, linesToJson(loaderLines), CATEGORY.loader);
            })
          }
        >
          {saving === "loader" ? "Saving…" : "Save loader"}
        </Button>
      </SectionCard>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert severity={toast.ok ? "success" : "error"} onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default BuyerAppDashboard;
